import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Box, 
  Grid, 
  useTheme, 
  Typography, 
  Alert, 
  Skeleton,
  Fade,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import EventOverview from './EventOverview';
import SummaryCards from './dashboard/SummaryCards';
import EventLineChart from './dashboard/EventLineChart';
import InsuranceDonutChart from './dashboard/InsuranceDonutChart';
import GaugeCharts from './dashboard/GaugeChart';
import LeadsTable from './dashboard/LeadsTable';

export default function AdminDashboard() {
  const [events, setEvents] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]); // Store ALL submissions
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const theme = useTheme();

  // Fetch data from API
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch events and submissions in parallel
      const [eventsRes, submissionsRes] = await Promise.all([
        axios.get('/api/events', { headers }).catch(() => ({ data: [] })),
        axios.get('/api/submissions', { headers }).catch(() => ({ data: [] }))
      ]);

      // Normalize event dates so downstream code has a single shape
      const eventsData = (eventsRes.data || []).map(e => ({
        ...e,
        startDate: e.startDate ?? e.start ?? null,
        endDate: e.endDate ?? e.end ?? null,
      }));
      const submissionsData = submissionsRes.data || [];

      setEvents(eventsData);
      setAllSubmissions(submissionsData); // Store all submissions

      // Set default selected event to the first active/ongoing or most recent event
      if (eventsData.length > 0 && !selectedEventId) {
        const defaultEvent =
          eventsData.find(e => ['Ongoing', 'Active'].includes(e.status)) || eventsData[0];
         setSelectedEventId(defaultEvent._id || defaultEvent.id);
       }

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      
      // Fallback to dummy data if API fails
      setEvents([
        { 
          _id: '1', 
          name: 'Summer Event', 
          start: '2025-07-01', 
          end: '2025-07-03', 
          location: 'Toronto', 
          status: 'Completed', 
          totalVisitors: 120,
          description: 'Summer insurance expo'
        },
        { 
          _id: '2', 
          name: 'Fall Event', 
          start: '2025-09-15', 
          end: '2025-09-17', 
          location: 'Georgina', 
          status: 'Ongoing', 
          totalVisitors: 45,
          description: 'Fall community fair'
        }
      ]);
      setSelectedEventId('1');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  };

  // Get selected event data
  const selectedEvent = useMemo(() => {
    if (!selectedEventId) return null;
    return events.find(e => String(e._id || e.id) === String(selectedEventId)) || null;
  }, [selectedEventId, events]);

  // IMPORTANT: Filter submissions by selected event
  const eventSubmissions = useMemo(() => {
    if (!selectedEventId || !allSubmissions.length) {
      return [];
    }

    // Filter submissions for the selected event
    return allSubmissions.filter(submission => {
      // Normalize eventId shape (string | {_id} | {id})
      if (submission.eventId) {
        const ev = submission.eventId;
        const evId = typeof ev === 'string' ? ev : (ev._id ?? ev.id);
        if (evId) return String(evId) === String(selectedEventId);
      }
      
      // If no eventId field, you might need to check other fields
      // For example, if submissions have eventName:
      if (submission.eventName && selectedEvent) {
        return submission.eventName === selectedEvent.name;
      }
      
      // If submissions don't have event association yet, 
      // you might want to return empty array or implement different logic
      return false;
    });
  }, [allSubmissions, selectedEventId, selectedEvent]);

  // Calculate dashboard metrics from FILTERED submissions
  const dashboardMetrics = useMemo(() => {
    const totalSubmissions = eventSubmissions.length; // Only count submissions for selected event
    const contractedCount = eventSubmissions.filter(s => s.status === 'Contracted').length;
    const newCount = eventSubmissions.filter(s => s.status === 'New').length;
    
    // Calculate submissions from last 7 days (for selected event only)
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const recentSubmissions = eventSubmissions.filter(s => 
      new Date(s.createdAt) > weekAgo
    ).length;

    // Conversion rate = Contracted / Total
    const conversionRate = totalSubmissions > 0
      ? ((contractedCount / totalSubmissions) * 100).toFixed(1)
      : 0;

    // Group submissions by insurance type (for selected event only)
    const insuranceTypeData = {};
    eventSubmissions.forEach(submission => {
      if (submission.insuranceTypes && Array.isArray(submission.insuranceTypes)) {
        submission.insuranceTypes.forEach(insurance => {
          const type = insurance.type || insurance;
          insuranceTypeData[type] = (insuranceTypeData[type] || 0) + 1;
        });
      }
    });

    // Convert to chart format
    const chartData = Object.entries(insuranceTypeData).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: totalSubmissions > 0 ? Math.round((count / totalSubmissions) * 100) : 0,
      leads: count
    }));

    return {
      totalSubmissions,
      contractedCount,
      newCount,
      recentSubmissions,
      conversionRate,
      chartData: chartData.length > 0 ? chartData : [
        { name: 'Auto', value: 0, leads: 0 },
        { name: 'Home', value: 0, leads: 0 },
        { name: 'Business', value: 0, leads: 0 },
        { name: 'Life', value: 0, leads: 0 }
      ]
    };
  }, [eventSubmissions]); // Only depend on filtered submissions

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      width: '100%',
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh'
    }}>
        <Box>
          {/* 1. Event Overview (Full Width) */}
          {loading ? (
            <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 2 }} />
          ) : (
            <EventOverview
              events={events}
              selectedEventId={selectedEventId}
              onEventChange={setSelectedEventId}
            />
          )}

          {/* 2. Summary Cards (Full Width) */}
          <Box sx={{ my: 3 }}>
            {loading ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4].map(i => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <SummaryCards 
                selectedEvent={selectedEvent} 
                dashboardMetrics={dashboardMetrics}
                submissions={eventSubmissions} // Pass filtered submissions
              />
            )}
          </Box>

          {/* 3. Charts Row (70/30 split) */}
          <Box sx={{ mb: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Skeleton variant="rectangular" height={360} sx={{ flex: '0 0 70%', borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={360} sx={{ flex: '0 0 30%', borderRadius: 2 }} />
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' },
                '& > *': {
                  minHeight: 360
                }
              }}>
                {/* Line Chart - 70% width */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', lg: '0 0 70%' },
                  minWidth: 0
                }}>
                  <EventLineChart 
                    height={360} 
                    selectedEvent={selectedEvent}
                    events={events}
                    submissions={allSubmissions} // Pass ALL submissions for comparison
                  />
                </Box>
                
                {/* Donut Chart - 30% width */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', lg: '0 0 30%' },
                  minWidth: 0
                }}>
                  <InsuranceDonutChart 
                    height={360} 
                    selectedEvent={selectedEvent}
                    data={dashboardMetrics.chartData}
                    submissions={eventSubmissions} // Pass filtered submissions
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* 4. Bottom Row (70/30 split) */}
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
                <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 70%' } }}>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4].map(i => (
                      <Grid item xs={12} sm={6} md={3} key={i}>
                        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 30%' } }}>
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { lg: 'stretch' }
              }}>
                {/* Gauge Charts - 70% width */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', lg: '0 0 70%' },
                  minWidth: 0
                }}>
                  <GaugeCharts 
                    selectedEvent={selectedEvent}
                    submissions={eventSubmissions} // Pass filtered submissions
                    dashboardMetrics={dashboardMetrics}
                  />
                </Box>
                
                {/* Leads Table - 30% width */}
                <Box sx={{ 
                  flex: { xs: '1 1 100%', lg: '0 0 30%' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <LeadsTable 
                    selectedEvent={selectedEvent}
                    submissions={eventSubmissions} // Pass filtered submissions
                    sx={{ 
                      flex: 1,
                      '& .MuiPaper-root': {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }
                    }} 
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
    </Box>
  );
}